import React, { useState } from "react";
import { createPortal } from "react-dom";
import withholdingTaxAPI from "../../api/withholdingTaxAPI";
import Button from "../Button";
import MoneyInput from "../MoneyInput";
import { AtcPicker, PayeePicker } from "./WhtPickers";
import notify from "../../lib/notify";
import useGuardedMutation from "../../hooks/useGuardedMutation";
import ConfirmModal from "../ConfirmModal";
import { MissingAccountBanner } from "./glUi";
import {
  computeTaxWithheldCentavos,
  formatCentavos,
  pesosToCentavos,
  percentToBasisPoints,
} from "../../utils/whtMoney";

// Edit one saved EWT record (PUT). The server re-validates and recomputes the
// tax; field errors come back in the batch shape with line 1.

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const inputClass =
  "w-full border border-ink-line rounded px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500";

// The stored snapshot is enough to show the payee and pick its ATCs.
const payeeFromRecord = (r) => r.counterparty && {
  _id: r.counterparty,
  name: r.counterpartyName,
  unassigned: false,
  profile: { payeeType: r.counterpartyClassification, tin9: r.counterpartyTin, tinBranch: r.counterpartyBranch },
};

const EwtRecordEditModal = ({ client, record, onClose, onSaved, onOpenPostingAccounts }) => {
  const [form, setForm] = useState(() => ({
    month: record.month,
    description: record.description || "",
    payee: payeeFromRecord(record),
    // The stored snapshot stands in for the picked option until another is picked.
    atc: record.atcCode ? { code: record.atcCode, rateBasisPoints: record.atcRateBasisPoints, description: "" } : null,
    atcPayeeType: record.counterpartyClassification || null,
    lastAtcCode: record.atcCode || null,
    atcCode: record.atcCode,
    atcRateBasisPoints: record.atcRateBasisPoints,
    rate: String((record.rateBasisPoints ?? 0) / 100),
    base: (record.baseCentavos || 0) / 100,
    invoiceNo: record.invoiceNo || "",
    withholdingOnly: record.glMode === "withholding_only",
    creditAccount: record.creditAccount || "cash",
  }));
  const [errors, setErrors] = useState({});
  const [confirmRepost, setConfirmRepost] = useState(false);
  const [missingAccount, setMissingAccount] = useState(null);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const baseCentavos = pesosToCentavos(form.base);
  const rateBp = percentToBasisPoints(form.rate);
  const tax = computeTaxWithheldCentavos(baseCentavos, rateBp);

  const save = async () => {
    setErrors({});
    setMissingAccount(null);
    setConfirmRepost(false);
    try {
      const res = await withholdingTaxAPI.updateRecord(client._id, record._id, {
        month: Number(form.month),
        description: form.description.trim(),
        counterparty: form.payee?._id,
        atcCode: form.atcCode,
        rateBasisPoints: rateBp,
        baseCentavos,
        invoiceNo: form.invoiceNo.trim(),
        glMode: form.withholdingOnly ? "withholding_only" : "full",
        creditAccount: form.creditAccount,
      });
      notify.success("Saved", "The withholding record was updated.");
      await onSaved(res.data, res.warnings || []);
    } catch (e) {
      if (e.lineErrors?.length) setErrors(e.lineErrors[0].fields || {});
      if (e.code === "POSTING_ACCOUNT_MISSING") setMissingAccount(e);
      notify.error("Couldn't save", e.message || "Try again.");
    }
  };
  const { mutate: runSave, isPending: saving } = useGuardedMutation(save);

  const err = (field) => errors[field] && <p role="alert" className="text-xs text-danger-500 mt-0.5">{errors[field]}</p>;

  return createPortal(
    <div className="fixed inset-0 bg-ink/50 flex items-center justify-center z-[9999] p-4">
      <div role="dialog" aria-modal="true" aria-labelledby="ewt-edit-title" className="bg-white rounded-xl border border-ink-line shadow-card-hover p-6 w-full max-w-2xl">
        <h2 id="ewt-edit-title" className="text-lg font-medium text-ink">Edit withholding record</h2>
        {record.isPostedToGL && <p className="text-xs text-ink-body mt-1">Posted to the GL — saving posts a reversal and a new entry.</p>}
        {missingAccount && <div className="mt-3"><MissingAccountBanner error={missingAccount} onOpenPostingAccounts={onOpenPostingAccounts} /></div>}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <label className="text-xs font-medium text-ink-body">Month
            <select value={form.month} onChange={(e) => set({ month: Number(e.target.value) })} className={`${inputClass} mt-1`}>
              {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
            </select>
            {err("month")}
          </label>
          <label className="text-xs font-medium text-ink-body">Description
            <input value={form.description} onChange={(e) => set({ description: e.target.value })} className={`${inputClass} mt-1`} />
          </label>
          <div className="text-xs font-medium text-ink-body">Payee
            <div className="mt-1">
              <PayeePicker
                clientId={client._id}
                value={form.payee}
                onChange={(payee) => setForm((f) => (!payee || !f.atc || f.atcPayeeType === payee.profile?.payeeType
                  ? { ...f, payee }
                  : { ...f, payee, atc: null, atcPayeeType: null, atcCode: "", lastAtcCode: null, atcRateBasisPoints: null, rate: "" }))}
              />
            </div>
            {err("counterparty")}
          </div>
          <div className="text-xs font-medium text-ink-body">ATC
            <div className="mt-1">
              <AtcPicker
                clientId={client._id}
                payeeType={form.payee?.profile?.payeeType}
                year={record.year}
                month={form.month}
                value={form.atc}
                // Same rule as the grid: a different code refills the rate, the same code keeps an override.
                onChange={(o) => setForm((f) => (o
                  ? {
                      ...f, atc: o, atcPayeeType: f.payee?.profile?.payeeType || null, atcCode: o.code, lastAtcCode: o.code,
                      atcRateBasisPoints: o.rateBasisPoints,
                      rate: o.code === (f.atcCode || f.lastAtcCode) && f.rate !== "" ? f.rate : String(o.rateBasisPoints / 100),
                    }
                  : { ...f, atc: null, atcPayeeType: null, atcCode: "", lastAtcCode: f.atcCode || f.lastAtcCode, atcRateBasisPoints: null }))}
              />
            </div>
            {err("atcCode")}
          </div>
          <label className="text-xs font-medium text-ink-body">Tax base (₱)
            <MoneyInput value={form.base} onValueChange={(v) => set({ base: v })} className="mt-1 w-full" />
            {err("baseCentavos")}
          </label>
          <label className="text-xs font-medium text-ink-body">Rate %
            <input inputMode="decimal" value={form.rate} onChange={(e) => set({ rate: e.target.value.replace(/[^\d.]/g, "") })} className={`${inputClass} mt-1 text-right`} />
            {err("rateBasisPoints")}
          </label>
          <label className="text-xs font-medium text-ink-body">Invoice / OR no.
            <input value={form.invoiceNo} onChange={(e) => set({ invoiceNo: e.target.value })} className={`${inputClass} mt-1`} />
          </label>
          <label className="text-xs font-medium text-ink-body">Credit to
            <select value={form.creditAccount} onChange={(e) => set({ creditAccount: e.target.value })} className={`${inputClass} mt-1`}>
              <option value="cash">Cash</option>
              <option value="ap">Accounts payable</option>
            </select>
          </label>
          <label className="md:col-span-2 flex items-center gap-2 text-sm text-ink">
            <input type="checkbox" checked={form.withholdingOnly} onChange={(e) => set({ withholdingOnly: e.target.checked })} className="h-4 w-4 accent-primary-500" />
            Already in the books? (withholding only)
          </label>
        </div>
        <p className="text-sm text-ink-body mt-4">
          Tax withheld: <span className="text-ink tabular-nums">₱{formatCentavos(tax)}</span>
        </p>
        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          {/* A posted row: saving reverses its entry and posts a new one (G1). */}
          <Button onClick={() => (record.isPostedToGL ? setConfirmRepost(true) : runSave())} loading={saving}>Save changes</Button>
        </div>
      </div>
      <ConfirmModal
        isOpen={confirmRepost}
        title="Edit a posted record?"
        message="This will post a reversal and a new entry."
        confirmLabel="Save and repost"
        variant="primary"
        loading={saving}
        onConfirm={() => runSave()}
        onCancel={() => setConfirmRepost(false)}
      />
    </div>,
    document.body
  );
};

export default EwtRecordEditModal;
